/**
 * setHrLineChart — pure data model for the per-set red-line HR chart.
 *
 * x-axis: 1-based length index (1..lapCount).
 * y-axis: BPM.
 *
 * One entry per lap (in order): bpm is the average HR of samples in
 * [lap.startMs, lap.endMs), or null if the lap has no HR samples.
 * connectNulls: false in the chart so the line BREAKS at HR-less laps.
 *
 * Returns null when the set window contains fewer than 4 HR samples total —
 * the same guard as hrBandChart, so the chart slot stays empty on HR-sparse swims.
 *
 * Pure module (no Svelte runtime, no ECharts import).
 */
import type { Lap } from "./laps";
import type { HrPoint } from "./setHrWindow";
import { hrSeriesInWindow } from "./setHrWindow";
import { strokeColor, STROKE_ABBREV } from "./designTokens";

const MIN_HR_PTS = 4;

export interface LengthHrEntry {
  /** 1-based length number (1..N). Used as the ECharts x value. */
  lengthIndex: number;
  /** Average BPM of HR samples in this length's time window, or null if no samples. */
  bpm: number | null;
  /** Hex color from strokeColor(). Used for the per-length avg dash. */
  color: string;
  /** Short label from STROKE_ABBREV (e.g. "FREE", "BR", "BK", "FLY"). */
  abbrev: string;
}

export interface SetHrLineChartData {
  /** One entry per lap (ordered 1..lapCount). bpm is null for HR-less laps. */
  lengths: LengthHrEntry[];
  avgBpm: number;
  peakBpm: number;
  minBpm: number;
  lapCount: number;
}

export function buildSetHrLineChart(
  laps: Lap[],
  hrSeries: readonly HrPoint[],
  setStartMs: number,
  setEndMs: number,
): SetHrLineChartData | null {
  if (laps.length === 0) return null;
  const span = setEndMs - setStartMs;
  if (span <= 0) return null;

  const inWindow = hrSeriesInWindow(hrSeries as HrPoint[], setStartMs, setEndMs);
  if (inWindow.length < MIN_HR_PTS) return null;

  // Global stats over all in-window samples
  let sum = 0;
  let peakBpm = -Infinity;
  let minBpm = Infinity;
  for (const p of inWindow) {
    sum += p.bpm;
    if (p.bpm > peakBpm) peakBpm = p.bpm;
    if (p.bpm < minBpm) minBpm = p.bpm;
  }
  const avgBpm = sum / inWindow.length;

  // Per-lap entries: one per lap in order, bpm null if no samples
  const lengths: LengthHrEntry[] = laps.map((lap, i) => {
    const lapSamples = hrSeriesInWindow(hrSeries as HrPoint[], lap.startMs, lap.endMs);
    const bpm =
      lapSamples.length === 0
        ? null
        : lapSamples.reduce((s, p) => s + p.bpm, 0) / lapSamples.length;
    return {
      lengthIndex: i + 1,
      bpm,
      color: strokeColor(lap.strokeStyle),
      abbrev: STROKE_ABBREV[lap.strokeStyle] ?? lap.strokeStyle,
    };
  });

  return {
    lengths,
    avgBpm,
    peakBpm,
    minBpm,
    lapCount: laps.length,
  };
}
